import { useEffect, useMemo, useState } from 'react';
import { useSession } from '../../lib/auth/session.js';
import { useCdrs, useExtensions, type CdrFilter } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, SearchInput, SectionHead } from '../../components/ui/Primitives.js';
import { Icon, type IconName } from '../../components/ui/Icon.js';
import { formatDuration, formatMoney, formatDateTime, timezoneAbbr } from '../../lib/format.js';
import { CallModal, readableNumber } from './CallDetail.js';

/**
 * Direction as a glyph with its word beside it for assistive tech.
 *
 * Read by shape down a column, which three same-length words in one colour do
 * not allow. The tone is kept from the old badge so nobody relearns the colours.
 */
const DIRECTION: Record<string, { icon: IconName; label: string; tone: string }> = {
  inbound: { icon: 'callIn', label: 'Inbound', tone: 'var(--arc-dir-in)' },
  outbound: { icon: 'callOut', label: 'Outbound', tone: 'var(--arc-dir-out)' },
  internal: { icon: 'callInternal', label: 'Internal', tone: 'var(--arc-fg-4)' },
};

/**
 * Periods, as days in the account's timezone. Africa/Johannesburg has no daylight saving, so its
 * day starts at +02:00 all year; the list is shown in that timezone already.
 */
type Period = 'today' | 'yesterday' | '7d' | '30d' | 'month' | 'last_month' | 'custom';
const PERIODS: Array<[Period, string]> = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days'],
  ['month', 'This month'], ['last_month', 'Last month'], ['custom', 'Choose dates…'],
];
const OFFSET = '+02:00';

const dayIn = (tz: string, d = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const addDays = (day: string, n: number): string => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const startOf = (day: string): string => `${day}T00:00:00${OFFSET}`;

/** The window a period covers: from its first day, up to the day after its last. */
function windowOf(p: Period, tz: string, custom: { from: string; to: string }): { from: string; to: string } | null {
  const today = dayIn(tz);
  const first = `${today.slice(0, 8)}01`;
  switch (p) {
    case 'today': return { from: startOf(today), to: startOf(addDays(today, 1)) };
    case 'yesterday': return { from: startOf(addDays(today, -1)), to: startOf(today) };
    case '7d': return { from: startOf(addDays(today, -6)), to: startOf(addDays(today, 1)) };
    case '30d': return { from: startOf(addDays(today, -29)), to: startOf(addDays(today, 1)) };
    case 'month': return { from: startOf(first), to: startOf(addDays(today, 1)) };
    case 'last_month': {
      const prev = `${addDays(first, -1).slice(0, 8)}01`;
      return { from: startOf(prev), to: startOf(first) };
    }
    case 'custom':
      if (!custom.from || !custom.to || custom.to < custom.from) return null;
      return { from: startOf(custom.from), to: startOf(addDays(custom.to, 1)) };
  }
}

export function CdrsPage({ tenantId: propTenantId, embedded }: {
  tenantId?: string;
  embedded?: boolean;
} = {}) {
  const activeTenantId = useSession((s) => s.activeTenantId);
  const can = useSession((s) => s.can);
  const tenantId = propTenantId ?? activeTenantId;
  // Tenant timezone, never the browser's -- a call at 09:00 in Windhoek must
  // not read as 10:00 because the admin is in Johannesburg (doc 07 §3).
  const tz = 'Africa/Johannesburg';

  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [period, setPeriod] = useState<Period>('7d');
  const [custom, setCustom] = useState(() => ({ from: addDays(dayIn(tz), -6), to: dayIn(tz) }));
  const [direction, setDirection] = useState('');
  const [result, setResult] = useState<'' | 'answered' | 'missed'>('');
  const [recorded, setRecorded] = useState(false);
  const [extensionId, setExtensionId] = useState('');
  // The call open in its window (CallDetail.tsx), if any.
  const [open, setOpen] = useState<string | null>(null);

  // Searched as typing pauses, not on every key.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const extensions = useExtensions(can('extension:read') && tenantId ? tenantId : null, { limit: 200 });
  const win = windowOf(period, tz, custom);
  const filter: CdrFilter = useMemo(() => ({
    q: q.length >= 2 ? q : undefined,
    direction: direction || undefined,
    answered: result === 'answered' ? true : result === 'missed' ? false : undefined,
    recorded: recorded || undefined,
    extension_id: extensionId || undefined,
    from: win?.from, to: win?.to,
  }), [q, direction, result, recorded, extensionId, win?.from, win?.to]);

  const calls = useCdrs(win ? tenantId : null, filter);
  const rows = calls.data?.pages.flatMap((p) => p.data) ?? [];
  const narrowed = !!(q || direction || result || recorded || extensionId || period !== '7d');
  const clear = () => {
    setSearch(''); setQ(''); setDirection(''); setResult(''); setRecorded(false); setExtensionId(''); setPeriod('7d');
  };

  if (!tenantId) {
    return <Empty icon="accounts" title="No account selected" hint="Choose an account to see its call history." />;
  }

  return (
    <>
      <SectionHead title="Call history" sub={`Times in ${timezoneAbbr(tz)}.`} />

      {calls.error && <ErrorBanner error={calls.error} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <SearchInput value={search} onChange={setSearch} label="Search calls"
            placeholder="Search number, name or extension" />
          <select className="arc-select" style={{ width: 'auto' }} value={period} aria-label="Period"
            onChange={(e) => setPeriod(e.target.value as Period)}>
            {PERIODS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          {period === 'custom' && (
            <span className="arc-row" style={{ gap: 6 }}>
              <input type="date" className="arc-input arc-date" aria-label="From" value={custom.from}
                max={custom.to || undefined} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
              <span className="arc-subtle">to</span>
              <input type="date" className="arc-input arc-date" aria-label="To" value={custom.to}
                min={custom.from || undefined} max={dayIn(tz)}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
            </span>
          )}
          <select className="arc-select" style={{ width: 'auto' }} value={direction}
            onChange={(e) => setDirection(e.target.value)} aria-label="Direction">
            <option value="">All directions</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="internal">Internal</option>
          </select>
          <select className="arc-select" style={{ width: 'auto' }} value={result}
            onChange={(e) => setResult(e.target.value as typeof result)} aria-label="Result">
            <option value="">Answered or not</option>
            <option value="answered">Answered</option>
            <option value="missed">Not answered</option>
          </select>
          {(extensions.data?.data.length ?? 0) > 0 && (
            <select className="arc-select" style={{ width: 'auto', maxWidth: 200 }} value={extensionId}
              onChange={(e) => setExtensionId(e.target.value)} aria-label="Extension">
              <option value="">All extensions</option>
              {extensions.data!.data.map((x) => (
                <option key={x.id} value={x.id}>{x.number}{x.display_name ? ` · ${x.display_name}` : ''}</option>
              ))}
            </select>
          )}
          <label className="arc-row arc-check" style={{ gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={recorded} onChange={(e) => setRecorded(e.target.checked)} />
            Recorded
          </label>
          <div className="arc-spacer" />
          {narrowed && (
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={clear}>Clear filters</button>
          )}
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Started</th>
                <th aria-label="Direction" style={{ width: 36 }} />
                <th>From</th>
                <th>To</th>
                <th className="arc-td-right">Duration</th>
                <th>Quality</th>
                <th className="arc-td-right">Cost</th>
                {/* Recordings have no page of their own: a recording is reached from its call. */}
                <th aria-label="Recording" style={{ width: 36 }} />
              </tr>
            </thead>
            {calls.data === undefined ? (
              <TableSkeleton cols={8} />
            ) : (
              <tbody>
                {rows.map((c) => {
                  const d = DIRECTION[c.direction] ?? DIRECTION['internal']!;
                  const fromName = c.from_name && !/^[\d+\s]+$/.test(c.from_name)
                    && !/^\d{2,6}-[a-z0-9.]+$/i.test(c.from_name) ? c.from_name : null;
                  return (
                    // The whole row opens the call; Enter does the same from the keyboard.
                    <tr key={c.id} onClick={() => setOpen(c.id)} style={{ cursor: 'pointer' }}
                      tabIndex={0} aria-label={`Open the call from ${readableNumber(c.from_number)}`}
                      onKeyDown={(e) => { if (e.key === 'Enter') setOpen(c.id); }}>
                      <td className="arc-num arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(c.started_at, tz)}
                      </td>
                      <td title={d.label}>
                        <span style={{ color: d.tone, display: 'inline-flex' }}>
                          <Icon name={d.icon} size={15} />
                        </span>
                        <span className="arc-sr-only">{d.label}</span>
                      </td>
                      <td>
                        {/* One of the account's own extensions: its name, whichever registration
                            placed the call (a desk phone or the app). The number is in the tooltip. */}
                        {c.from_extension
                          ? <span title={`Extension ${c.from_extension.number}`}>{c.from_extension.name}</span>
                          : <>
                              <span className="arc-num">{readableNumber(c.from_number)}</span>
                              {fromName && <span className="arc-subtle"> · {fromName}</span>}
                            </>}
                        {/* What the agent said it was (0108), under the caller rather than in a
                            column of its own: most calls have none, and an empty column on every
                            row costs more width than the few that do are worth. */}
                        {c.disposition && (
                          <div className="arc-pause-chip arc-disp" data-kind={c.disposition.kind}
                            title={c.disposition.note ?? undefined}>
                            {c.disposition.label}
                            {c.disposition.note && <Icon name="edit" size={10} />}
                          </div>
                        )}
                      </td>
                      <td className={c.to_extension ? undefined : 'arc-num'}>
                        {c.to_extension
                          ? <span title={`Extension ${c.to_extension.number}`}>{c.to_extension.name}</span>
                          : readableNumber(c.to_number ?? c.destination_e164)}
                      </td>
                      <td className="arc-td-right arc-num">
                        {c.answered_at ? formatDuration(c.duration_sec) : <span className="arc-subtle">no answer</span>}
                      </td>
                      <td>{c.mos ? <MosIndicator mos={c.mos} /> : <span className="arc-subtle">—</span>}</td>
                      <td className="arc-td-right arc-num">
                        {/* Over the client's own trunk nothing is charged; their carrier's cost shows instead (0131). */}
                        {c.sell_amount === null && c.carrier_cost
                          ? <span className="arc-subtle" title="Your carrier’s charge, from your rate sheet. Not charged by us.">{formatMoney(c.carrier_cost, c.currency)}</span>
                          : formatMoney(c.sell_amount, c.currency)}
                      </td>
                      <td title={!c.has_recording ? undefined
                        : c.recording_status === 'saving' ? 'Recorded — still being saved to storage'
                        : c.recording_status === 'unavailable' ? 'Recorded — the file is not in storage'
                        : 'Recorded — open the call to play it'}>
                        {c.has_recording && (
                          <span className="arc-subtle" style={{ display: 'inline-flex' }}>
                            <Icon name="play" size={13} />
                            <span className="arc-sr-only">Recorded</span>
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>

        {!calls.isLoading && rows.length > 0 && (
          <div className="arc-row arc-list-foot">
            <span className="arc-subtle arc-num">{rows.length} {rows.length === 1 ? 'call' : 'calls'}{calls.hasNextPage ? ' so far' : ''}</span>
            <div className="arc-spacer" />
            {calls.hasNextPage && (
              <button type="button" className="arc-btn arc-btn-sm" disabled={calls.isFetchingNextPage}
                onClick={() => void calls.fetchNextPage()}>
                {calls.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            )}
          </div>
        )}

        {!win && (
          <Empty icon="clock" title="Choose two dates" hint="The second date has to be on or after the first." />
        )}
        {win && calls.data !== undefined && rows.length === 0 && (
          narrowed
            ? <Empty icon="search" title="No calls match" hint="Try a longer period, or clear the filters."
                action={<button type="button" className="arc-btn arc-btn-sm" onClick={clear}>Clear filters</button>} />
            : <Empty icon="phone" title="No calls in the last 7 days"
                hint="Call records appear here once the switch ships them. Dial 9196 from a registered handset to make one." />
        )}
      </div>

      {open && <CallModal tenantId={tenantId} callId={open} tz={tz} onClose={() => setOpen(null)} />}
    </>
  );
}

/** MOS with a label as well as a colour, so it does not rely on hue alone. */
function MosIndicator({ mos }: { mos: string }) {
  const v = Number(mos);
  const tone = v >= 4.0 ? 'ok' : v >= 3.5 ? 'warn' : 'danger';
  const label = v >= 4.0 ? 'good' : v >= 3.5 ? 'fair' : 'poor';
  // Good is what every call should be: said plainly, so the fair and poor ones stand out.
  if (tone === 'ok') return <span className="arc-num" title="Call quality: good">{mos}</span>;
  return (
    <Badge tone={tone}>
      <span className="arc-dot" aria-hidden="true" />
      {mos} {label}
    </Badge>
  );
}
